import { useCallback } from 'react';
import {
  getFirestore,
  doc,
  getDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { dedupeLikes } from '../utils/songUtils';
import logger from '../utils/logger';

const db = getFirestore();

const useSongLikes = (setSongs) => {
  const likeSong = useCallback(async (songId, userId) => {
    logger.debug(`Liking song: ${songId} by user: ${userId}`);
    const songRef = doc(db, 'songs', songId);
    const userRef = doc(db, 'users', userId);

    try {
      const songSnapshot = await getDoc(songRef);
      if (!songSnapshot.exists()) throw new Error('Song not found');
      const songData = songSnapshot.data();

      const userSnapshot = await getDoc(userRef);
      if (!userSnapshot.exists()) throw new Error('User not found');
      const userData = userSnapshot.data();

      const batch = writeBatch(db);
      batch.update(songRef, { likes: arrayUnion(userId) });
      batch.update(userRef, { likedSongs: arrayUnion({ songId, likedAt: Timestamp.now() }) });
      await batch.commit();

      try {
        await httpsCallable(functions, 'likeSongNotification')({
          songId,
          songData: {
            URL: songData.URL,
            songName: songData.songName,
            songArtist: songData.songArtist,
            thumbnailURL: songData.thumbnailURL,
          },
          userData: { userName: userData.userName },
        });
        logger.debug('Notification sent successfully');
      } catch (notificationError) {
        logger.error('Failed to send notification:', notificationError);
      }

      setSongs((prevSongs) =>
        prevSongs.map((song) => {
          if (song.id !== songId) return song;
          const existingLikes = dedupeLikes(song.likes);
          if (existingLikes.includes(userId)) {
            return existingLikes === song.likes ? song : { ...song, likes: existingLikes };
          }
          return { ...song, likes: [...existingLikes, userId] };
        })
      );

      logger.info(`Song liked successfully: ${songId}`);
      return true;
    } catch (error) {
      logger.error('Error liking the song:', error);
      throw error;
    }
  }, [setSongs]);

  const unlikeSong = useCallback(async (songId, userId) => {
    const songRef = doc(db, 'songs', songId);
    const userRef = doc(db, 'users', userId);

    try {
      const batch = writeBatch(db);
      batch.update(songRef, { likes: arrayRemove(userId) });

      const userSnapshot = await getDoc(userRef);
      const userData = userSnapshot.data();
      const likedSongObject = userData.likedSongs?.find((obj) => obj.songId === songId);
      if (likedSongObject) {
        batch.update(userRef, { likedSongs: arrayRemove(likedSongObject) });
      }

      await batch.commit();

      setSongs((prevSongs) =>
        prevSongs.map((song) =>
          song.id === songId
            ? { ...song, likes: (song.likes || []).filter((id) => id !== userId) }
            : song
        )
      );

      try {
        await httpsCallable(functions, 'deleteLikeNotification')({ songId });
      } catch (error) {
        logger.error('Error deleting like notification:', error);
      }
    } catch (error) {
      logger.error('Error unliking the song:', error);
    }
  }, [setSongs]);

  return { likeSong, unlikeSong };
};

export default useSongLikes;
